'use client';

import { DateTime } from 'luxon';
import type { ReactElement, ReactNode } from 'react';
import { useCallback, useMemo, useState } from 'react';

import { TimeFormat } from '@repo/ui/constants/timezone';
import { SSRSupportedLocalStorage } from '@repo/ui/utils/local-storage';

import { TimezoneContext } from './timezone-context';

export const TimezoneProvider = ({ children }: { children: ReactNode }): ReactElement => {
  const [timezone, setTimezone] = useState<string>(() => {
    return SSRSupportedLocalStorage.getItem('preferredTimezone') || DateTime.local().zoneName;
  });

  const [timeFormat, setTimeFormat] = useState<TimeFormat>(() => {
    return (SSRSupportedLocalStorage.getItem('preferredTimeFormat') as TimeFormat | null) || TimeFormat['24hr'];
  });

  const updateTimezone = useCallback((newTimezone: string) => {
    SSRSupportedLocalStorage.setItem('preferredTimezone', newTimezone);
    setTimezone(newTimezone);
  }, []);

  const updateTimeFormat = useCallback((format: TimeFormat) => {
    SSRSupportedLocalStorage.setItem('preferredTimeFormat', format);
    setTimeFormat(format);
  }, []);

  const value = useMemo(
    () => ({ timezone, updateTimezone, timeFormat, updateTimeFormat }),
    [timezone, updateTimezone, timeFormat, updateTimeFormat],
  );

  return <TimezoneContext.Provider value={value}>{children}</TimezoneContext.Provider>;
};
